import { Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { displayUrl, qrSvg } from './qr';

interface PrintQrProps {
  url: string;
  localName: string;
  logoUrl: string | null;
  primaryColor: string;
  disabled?: boolean;
  /** Se llama al tocar "Imprimir" (p. ej. para marcar el paso del QR como hecho). */
  onPrint?: () => void;
}

const escapeHtml = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

function printDocument({ url, localName, logoUrl, primaryColor }: PrintQrProps, svg: string): string {
  const color = /^#[0-9a-fA-F]{6}$/.test(primaryColor) ? primaryColor : '#111111';
  const logo = logoUrl ? `<img class="logo" src="${escapeHtml(logoUrl)}" alt="" />` : '';
  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>QR · ${escapeHtml(localName)}</title>
<style>
  @page { size: A4 portrait; margin: 16mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; background: #fff; color: #111; font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
  .sheet { min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8mm; padding: 12mm; text-align: center; }
  .logo { max-height: 32mm; max-width: 70mm; object-fit: contain; }
  h1 { margin: 0; font-size: 30pt; line-height: 1.1; color: ${color}; }
  .qr { width: 120mm; height: 120mm; padding: 4mm; border: 1.5mm solid ${color}; border-radius: 6mm; }
  .qr svg { width: 100%; height: 100%; display: block; }
  .cta { margin: 0; font-size: 22pt; font-weight: 700; }
  .url { margin: 0; font-size: 13pt; color: #555; word-break: break-all; }
  .actions { position: fixed; top: 12px; right: 12px; display: flex; gap: 8px; }
  .actions button { font: inherit; padding: 8px 14px; border-radius: 8px; border: 1px solid #ccc; background: #fff; cursor: pointer; }
  .actions button.primary { background: #111; color: #fff; border-color: #111; }
  @media print { .actions { display: none; } .sheet { min-height: auto; height: 100%; } }
</style>
</head>
<body>
  <div class="actions">
    <button type="button" onclick="window.close()">Cerrar</button>
    <button type="button" class="primary" onclick="window.print()">Imprimir</button>
  </div>
  <main class="sheet">
    ${logo}
    <h1>${escapeHtml(localName)}</h1>
    <div class="qr">${svg}</div>
    <p class="cta">Escaneá para ver el menú</p>
    <p class="url">${escapeHtml(displayUrl(url))}</p>
  </main>
  <script>
    (function () {
      var imgs = Array.prototype.slice.call(document.images);
      var pending = imgs.filter(function (i) { return !i.complete; });
      var done = false;
      function go() { if (done) return; done = true; setTimeout(function () { window.focus(); window.print(); }, 150); }
      if (!pending.length) return go();
      var left = pending.length;
      pending.forEach(function (i) { i.addEventListener('load', next); i.addEventListener('error', function () { i.remove(); next(); }); });
      function next() { left -= 1; if (left <= 0) go(); }
      setTimeout(go, 3000);
    })();
  </script>
</body>
</html>`;
}

/** Abre una ventana con la hoja para imprimir (logo, nombre, QR grande y llamado a escanear). */
export async function openPrintView(props: PrintQrProps) {
  const win = window.open('', '_blank', 'width=820,height=1100');
  if (!win) {
    toast.error('Tu navegador bloqueó la ventana. Permití las ventanas emergentes para este sitio.');
    return;
  }
  try {
    const svg = await qrSvg(props.url);
    win.document.open();
    win.document.write(printDocument(props, svg));
    win.document.close();
  } catch {
    win.close();
    toast.error('No pudimos generar el QR para imprimir');
  }
}

export function PrintQrButton(props: PrintQrProps) {
  return (
    <Button
      type="button"
      variant="outline"
      disabled={props.disabled}
      onClick={() => {
        props.onPrint?.();
        void openPrintView(props);
      }}
    >
      <Printer /> Imprimir
    </Button>
  );
}
